import * as React from 'react';
import {useQuery} from "@tanstack/react-query";
import {ResidenceExplorerService} from "features/residenceExplorer/services/service";
import {ResidenceExplorerServicQueryKeys} from "features/residenceExplorer/services/config";
import {MainGrid} from "@atoms/Layout/MainGrid";
import {FormattedMessage} from "react-intl";
import {P} from "@atoms/Text/P";

import './style.scss';
import {Button, ButtonColor, ButtonSize, ButtonType} from "@atoms/LinksAndButtons/Button";
import {Icon} from "@atoms/Media/Icon";
import { getBackgroundImageUrlFromCDN } from '@atoms/Media/AsyncImage/helper';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface Props {
    stageId: string;
    objectId?: string;
}

export const ExternalExplorerBlock : React.FunctionComponent<Props> = ({stageId, objectId}) => {

    const {data, isPending} = useQuery({
        queryKey: ResidenceExplorerServicQueryKeys.getExternalData(stageId),
        queryFn: async () => {
            return await ResidenceExplorerService.getExternalData(stageId);
        },
    })

    if(isPending || (data && objectId && !data.objects.find(x => x.number === objectId))) return <div></div>;

    const isStage = stageId && !objectId;

    const getExternalImage = () => {
        const stageImageUrl =   `/externalfiles/image/residenceexplorer/${stageId}/external/${data?.stageImage}`;
        const stageCdnImageUrl = getBackgroundImageUrlFromCDN(stageImageUrl, 1280, true);

        if(stageId && !objectId) {
            return (
                <div
                    className="external-explorer-block__backdrop"
                    style={{background: `url('${stageCdnImageUrl}') center center no-repeat`, backgroundSize: 'cover'}}
                />
            )
        }

        if(objectId) {
            const object = data?.objects.find(x => x.number === objectId);

            if(!object) return null;

            const objectImageUrl =   `/externalfiles/image/residenceexplorer/${stageId}/external/objects/${object.imageUrl}`;
            const objectCdnImageUrl = getBackgroundImageUrlFromCDN(objectImageUrl, 1280, true);

            return (
                <div
                    className="external-explorer-block__backdrop"
                    style={{background: `url('${objectCdnImageUrl}') center center no-repeat`, backgroundSize: 'cover'}}
                />
            )
        }

        return null;

    }

    const onStartClick = () => {
        if(stageId && !objectId) {
            window.open(`${data?.stageExternalUrl}?autostart=true`, '_blank', 'noopener,noreferrer')
        }

        if(objectId) {

            const object = data?.objects.find(x => x.number === objectId);

            if(!object) return null;

            window.open(`${object.url}?autostart=true`, '_blank', 'noopener,noreferrer')

        }

        return null;

    }


    return (
        <MainGrid noGap fullwidth>
            <div className="external-explorer-block">
                <div  className="external-explorer-block__header">
                    <Headline level={HeadlineLevel.H2}>
                        {!isStage && <FormattedMessage id="jm.features.appartmentViewer.seeApartmentIn360" />}
                        {isStage && <FormattedMessage id="jm.features.appartmentViewer.seeBlockIn360" />}
                    </Headline>

                    <P>
                        {!isStage && <FormattedMessage id="jm.features.appartmentViewer.disclaimerText"/>}
                        {isStage && <FormattedMessage id="jm.features.appartmentViewer.blockDisclaimerText" />}
                    </P>
                </div>

                <div className="external-explorer-block__content">
                    <div className="wrapper">
                        {getExternalImage()}

                        <div className="external-explorer-block__intro">
                            <Headline level={HeadlineLevel.H2}>
                                {!isStage && <FormattedMessage id="jm.features.appartmentViewer.welcome" />}
                                {isStage && <FormattedMessage id="jm.features.appartmentViewer.blockLookAround" />}
                            </Headline>

                            {!isStage &&
                                <P>
                                    <FormattedMessage id="jm.features.appartmentViewer.navigationIntro" />
                                </P>
                            }

                            <Button
                                className="apartment-viewer-intro__button"
                                onClick={onStartClick}
                                icon={<Icon name="apartment-viewer-play" size={40} />}
                                buttonColor={ButtonColor.DEFAULT}
                                buttonSize={ButtonSize.SMALL}
                                buttonType={ButtonType.GHOST}
                            >
                                {!isStage && <FormattedMessage id="jm.features.appartmentViewer.visitApartment" />}
                                {isStage && <FormattedMessage id="jm.features.appartmentViewer.visitBlock" />}
                            </Button>
                        </div>
                    </div>
                </div>



            </div>
        </MainGrid>
    )
}
